import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import {
  LuContact, LuFileText, LuFileCode, LuReceipt, LuDownload, LuSearch, LuInfo, LuAlertTriangle, LuFileSpreadsheet, LuShield,
} from 'react-icons/lu';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from 'recharts';
import {
  exportEspelhoIndividual, exportEspelhoExcel, exportComprovante,
} from './exportUtils';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateBR(str) {
  if (!str) return '—';
  return new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

function fmtDateLong(str) {
  if (!str) return '—';
  return new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

// Mês corrente: do dia 1 ao dia de hoje
function defaultPeriod() {
  const now  = new Date();
  const y    = now.getFullYear();
  const m    = String(now.getMonth() + 1).padStart(2, '0');
  const d    = String(now.getDate()).padStart(2, '0');
  return { date_from: `${y}-${m}-01`, date_to: `${y}-${m}-${d}` };
}

const EVENT_TYPES = [
  { key: 'clock_in',  label: 'Entrada',          color: 'var(--success)', bg: 'var(--success-soft)' },
  { key: 'lunch_out', label: 'Saída p/ Almoço',   color: 'var(--warning)', bg: 'var(--warning-soft)' },
  { key: 'lunch_in',  label: 'Retorno do Almoço', color: 'var(--info)', bg: 'var(--info-soft)' },
  { key: 'clock_out', label: 'Saída Geral',        color: 'var(--danger)', bg: 'var(--danger-soft)' },
];

// ─── Sub-componente: Filtros ──────────────────────────────────────────────────

function FilterForm({ filters, setFilters, employees, departments, showDept, empRequired }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:16, alignItems:'flex-end' }}>
      <div>
        <label className="form-label">Funcionário {empRequired ? '*' : ''}</label>
        <select className="form-control" style={{ minWidth:200 }}
          value={filters.employee_id} onChange={(e) => set('employee_id', e.target.value)}>
          <option value="">{empRequired ? '— selecione —' : '— todos —'}</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.full_name}</option>
          ))}
        </select>
      </div>
      {showDept && (
        <div>
          <label className="form-label">Departamento</label>
          <select className="form-control" style={{ minWidth:160 }}
            value={filters.department_id} onChange={(e) => set('department_id', e.target.value)}>
            <option value="">— todos —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="form-label">Data Inicial *</label>
        <input type="date" className="form-control" value={filters.date_from}
          onChange={(e) => set('date_from', e.target.value)} />
      </div>
      <div>
        <label className="form-label">Data Final *</label>
        <input type="date" className="form-control" value={filters.date_to}
          onChange={(e) => set('date_to', e.target.value)} />
      </div>
    </div>
  );
}

// ─── Sub-componente: Info box ─────────────────────────────────────────────────

function InfoBox({ children }) {
  return (
    <div style={{ display:'flex', gap:10, background:'var(--info-soft)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--info)' }}>
      <LuInfo style={{ flexShrink:0, marginTop:1 }} />
      <span>{children}</span>
    </div>
  );
}

function WarnBox({ children }) {
  return (
    <div style={{ display:'flex', gap:10, background:'var(--warning-soft)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--warning)' }}>
      <LuAlertTriangle style={{ flexShrink:0, marginTop:1 }} />
      <span>{children}</span>
    </div>
  );
}

function SignBox() {
  return (
    <div style={{ display:'flex', gap:10, background:'var(--purple-soft)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--purple)' }}>
      <LuShield style={{ flexShrink:0, marginTop:1 }} />
      <span>
        <strong>Assinatura digital:</strong> a assinatura CAdES (.p7s) exigida pelo art. 74 da Portaria 671/2021 requer
        integração com um certificado digital ICP-Brasil. Os arquivos gerados aqui já possuem o layout correto e podem
        ser submetidos a um serviço de assinatura certificado.
      </span>
    </div>
  );
}

// ─── Gráfico: Horas Extras por Dia ────────────────────────────────────────────
// Série temporal (uma barra por dia do período filtrado) — tipo de gráfico
// escolhido pelo tipo de dado (evolução diária de uma grandeza numérica),
// não pizza. Dias com ocorrência/violação CLT no dia ficam em vermelho para
// destacar a correlação entre hora extra e alerta.

function OvertimeChart({ records, chartRef }) {
  const data = (records || [])
    .filter((r) => !r.incomplete)
    .map((r) => ({
      date:         fmtDateBR(r.work_date),
      horas:        Math.round(((r.overtime_minutes || 0) / 60) * 100) / 100,
      hasViolation: (r.violations?.length || 0) > 0,
    }));

  if (!data.length) return null;

  return (
    <div ref={chartRef} style={{ background:'var(--surface-2)', borderRadius:8, border:'1px solid var(--border)', padding:'14px 16px', marginBottom:20 }}>
      <div style={{ fontSize:12, fontWeight:700, color:'var(--text-1)', marginBottom:4 }}>Horas Extras por Dia</div>
      <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:10 }}>
        Barras em vermelho indicam dia com ocorrência/alerta CLT registrado
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top:5, right:10, left:0, bottom:5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2c30" />
          <XAxis dataKey="date" tick={{ fontSize:11, fill:'#9aa0a6' }} />
          <YAxis tick={{ fontSize:11, fill:'#9aa0a6' }} allowDecimals={false}
            label={{ value:'horas', angle:-90, position:'insideLeft', fontSize:11, fill:'#7c8087' }} />
          <Tooltip formatter={(v) => [`${v}h`, 'Horas extras']} labelStyle={{ fontWeight:700 }} />
          <Bar dataKey="horas" radius={[4, 4, 0, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.hasViolation ? '#f05252' : '#8ED400'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Aba: Espelho de Ponto ────────────────────────────────────────────────────

function TabEspelho({ filters, setFilters, employees, departments }) {
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [espelhoData,   setEspelhoData]   = useState(null);
  const [espelhoRec,    setEspelhoRec]    = useState([]);
  const [exporting,     setExporting]     = useState('');
  const chartRef = useRef(null);

  const handleGerar = async () => {
    if (!filters.employee_id) { setError('Selecione um funcionário'); return; }
    if (!filters.date_from || !filters.date_to) { setError('Informe o período'); return; }
    setError(''); setLoading(true);
    try {
      const [repRes, recRes] = await Promise.all([
        api.get('/time-records/report', { params: filters }),
        api.get('/time-records',        { params: filters }),
      ]);
      if (!repRes.data.length) { setError('Nenhum registro encontrado para este período'); setLoading(false); return; }
      setEspelhoData(repRes.data[0]);
      setEspelhoRec(recRes.data);
    } catch { setError('Erro ao carregar dados'); }
    finally   { setLoading(false); }
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    try { await exportEspelhoIndividual(espelhoData, espelhoRec, filters, { chartEl: chartRef.current }); }
    catch { setError('Erro ao gerar PDF'); }
    finally { setExporting(''); }
  };

  const handleExportExcel = async () => {
    setExporting('xlsx');
    try { await exportEspelhoExcel(espelhoData, espelhoRec, filters, { chartEl: chartRef.current }); }
    catch { setError('Erro ao gerar Excel'); }
    finally { setExporting(''); }
  };

  const s = espelhoData?.summary || {};

  return (
    <div>
      <InfoBox>
        Espelho de Ponto Individual conforme Portaria MTE 671/2021, art. 83 e 84.
        Deve ser disponibilizado ao trabalhador e ao fiscal do trabalho quando solicitado.
      </InfoBox>
      <FilterForm filters={filters} setFilters={setFilters} employees={employees}
        departments={departments} showDept={false} empRequired />

      {error && <div className="alert-custom danger" style={{ marginBottom:12 }}>{error}</div>}

      <button onClick={handleGerar} disabled={loading}
        style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer',
          fontSize:13, fontWeight:700, fontFamily:'inherit',
          background:'var(--surface-3)', color:'var(--text-1)', display:'flex', alignItems:'center', gap:6,
          opacity: loading ? 0.7 : 1, marginBottom:20 }}>
        <LuSearch /> {loading ? 'Carregando…' : 'Gerar Espelho'}
      </button>

      {espelhoData && (
        <>
          {/* KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:10, marginBottom:16 }}>
            {[
              ['Funcionário',       espelhoData.full_name],
              ['Departamento',      espelhoData.department],
              ['Dias Trabalhados',  s.complete_records || 0],
              ['Pontualidade',      s.punctuality_pct != null ? `${s.punctuality_pct}%` : '—'],
              ['Total Trabalhado',  s.total_worked_fmt   || '—'],
              ['Horas Extras',      s.total_overtime_fmt || '—'],
              ['Alertas CLT',       s.total_violations > 0 ? `${s.total_violations}` : 'Nenhum'],
            ].map(([label, val]) => (
              <div key={label} style={{ background:'var(--surface-2)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:10, color:'var(--text-3)', fontWeight:600, marginBottom:2 }}>{label}</div>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--text-1)' }}>{val}</div>
              </div>
            ))}
          </div>

          {s.clocked_in_records > 0 && (
            <InfoBox>
              Pontualidade considera entrada registrada até 08:05 (jornada padrão 08h00 + tolerância de
              5 min — CLT art. 58 §1) sobre {s.clocked_in_records} marcaç{s.clocked_in_records !== 1 ? 'ões' : 'ão'} de
              entrada no período.
            </InfoBox>
          )}

          {/* Gráfico de horas extras por dia */}
          <OvertimeChart records={espelhoData.records} chartRef={chartRef} />

          {/* Botões de exportação */}
          <div style={{ display:'flex', gap:10, marginBottom:16 }}>
            <button onClick={handleExportPDF} disabled={!!exporting}
              style={{ padding:'8px 18px', border:'none', borderRadius:8, cursor:'pointer',
                fontSize:13, fontWeight:700, fontFamily:'inherit',
                background:'var(--danger)', color:'var(--on-solid)', display:'flex', alignItems:'center', gap:6,
                opacity: exporting ? 0.7 : 1 }}>
              <LuFileText /> {exporting === 'pdf' ? 'Gerando…' : 'Baixar PDF'}
            </button>
            <button onClick={handleExportExcel} disabled={!!exporting}
              style={{ padding:'8px 18px', border:'none', borderRadius:8, cursor:'pointer',
                fontSize:13, fontWeight:700, fontFamily:'inherit',
                background:'var(--success)', color:'var(--on-solid)', display:'flex', alignItems:'center', gap:6,
                opacity: exporting ? 0.7 : 1 }}>
              <LuFileSpreadsheet /> {exporting === 'xlsx' ? 'Gerando…' : 'Baixar Excel'}
            </button>
          </div>

          {/* Tabela de registros */}
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ borderBottom:'2px solid var(--border)' }}>
                  {['Data','NSR','Entrada','S. Almoço','Retorno','Saída','Trabalhado','H. Extra','Ocorrências'].map((h) => (
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', color:'var(--text-3)', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {espelhoRec.map((r) => (
                  <tr key={r.id} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--text-1)', whiteSpace:'nowrap' }}>{fmtDateBR(r.work_date)}</td>
                    <td style={{ padding:'8px 10px', color:'var(--text-3)', fontFamily:'monospace', fontSize:11 }}>{r.nsr ? String(r.nsr).padStart(9,'0') : '—'}</td>
                    <td style={{ padding:'8px 10px', color:'var(--success)' }}>{fmt(r.clock_in)}</td>
                    <td style={{ padding:'8px 10px', color:'var(--text-2)' }}>{fmt(r.lunch_out)}</td>
                    <td style={{ padding:'8px 10px', color:'var(--text-2)' }}>{fmt(r.lunch_in)}</td>
                    <td style={{ padding:'8px 10px', color:'var(--danger)' }}>{fmt(r.clock_out)}</td>
                    <td style={{ padding:'8px 10px', fontWeight:600 }}>{r.incomplete ? <em style={{color:'var(--text-3)'}}>Em aberto</em> : (r.worked_fmt || '—')}</td>
                    <td style={{ padding:'8px 10px', color: r.overtime_minutes > 0 ? 'var(--danger)' : 'var(--text-3)', fontWeight: r.overtime_minutes > 0 ? 700 : 400 }}>
                      {r.overtime_minutes > 0 ? r.overtime_fmt : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', fontSize:11, color: r.violations?.length ? 'var(--danger)' : 'var(--success)' }}>
                      {r.violations?.length ? r.violations.join('; ') : (r.has_night_work ? 'Noturno' : 'OK')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Aba: AFD ────────────────────────────────────────────────────────────────

function TabAFD({ filters, setFilters, employees, departments }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleDownload = async () => {
    if (!filters.date_from || !filters.date_to) { setError('Informe o período'); return; }
    setError(''); setLoading(true);
    try {
      const params = {};
      if (filters.date_from)    params.date_from    = filters.date_from;
      if (filters.date_to)      params.date_to      = filters.date_to;
      if (filters.employee_id)  params.employee_id  = filters.employee_id;
      if (filters.department_id) params.department_id = filters.department_id;

      const res = await api.get('/time-records/afd', { params, responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `AFD-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Erro ao gerar AFD'); }
    finally   { setLoading(false); }
  };

  return (
    <div>
      <InfoBox>
        AFD gerado conforme Portaria MTE 671/2021, Anexo V (REP-P).
        Contém os tipos de registro 1 (cabeçalho), 2 (marcações) e 9 (trailer)
        no formato fixo com encoding ISO-8859-1 e terminações CR+LF.
      </InfoBox>
      <SignBox />
      <FilterForm filters={filters} setFilters={setFilters} employees={employees}
        departments={departments} showDept empRequired={false} />
      {error && <div className="alert-custom danger" style={{ marginBottom:12 }}>{error}</div>}
      <button onClick={handleDownload} disabled={loading}
        style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer',
          fontSize:13, fontWeight:700, fontFamily:'inherit',
          background:'var(--accent)', color:'var(--on-accent)', display:'flex', alignItems:'center', gap:6,
          opacity: loading ? 0.7 : 1 }}>
        <LuDownload /> {loading ? 'Gerando…' : 'Baixar AFD (.txt)'}
      </button>
    </div>
  );
}

// ─── Aba: AEJ ────────────────────────────────────────────────────────────────

function TabAEJ({ filters, setFilters, employees, departments }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleDownload = async () => {
    if (!filters.date_from || !filters.date_to) { setError('Informe o período'); return; }
    setError(''); setLoading(true);
    try {
      const params = {};
      if (filters.date_from)    params.date_from    = filters.date_from;
      if (filters.date_to)      params.date_to      = filters.date_to;
      if (filters.employee_id)  params.employee_id  = filters.employee_id;
      if (filters.department_id) params.department_id = filters.department_id;

      const res = await api.get('/time-records/aej', { params, responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `AEJ-${new Date().toISOString().slice(0, 10)}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Erro ao gerar AEJ'); }
    finally   { setLoading(false); }
  };

  return (
    <div>
      <InfoBox>
        AEJ gerado conforme Portaria MTE 671/2021, Anexo VI (SREP — sistema de software).
        Contém os tipos 1 (cabeçalho), 2 (trabalhadores), 3 (jornada contratada),
        4 (marcações) e 9 (trailer). Jornada padrão: 08h00–12h00 / 13h00–17h00, Seg–Sex.
      </InfoBox>
      <SignBox />
      <FilterForm filters={filters} setFilters={setFilters} employees={employees}
        departments={departments} showDept empRequired={false} />
      {error && <div className="alert-custom danger" style={{ marginBottom:12 }}>{error}</div>}
      <button onClick={handleDownload} disabled={loading}
        style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer',
          fontSize:13, fontWeight:700, fontFamily:'inherit',
          background:'var(--accent)', color:'var(--on-accent)', display:'flex', alignItems:'center', gap:6,
          opacity: loading ? 0.7 : 1 }}>
        <LuDownload /> {loading ? 'Gerando…' : 'Baixar AEJ (.txt)'}
      </button>
    </div>
  );
}

// ─── Aba: Comprovante de Marcação ─────────────────────────────────────────────

function TabComprovante({ filters, setFilters, employees, departments }) {
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [records,  setRecords]  = useState([]);
  const [exporting, setExporting] = useState(null); // {id, key} do comprovante em geração

  const handleBuscar = async () => {
    if (!filters.employee_id)               { setError('Selecione um funcionário'); return; }
    if (!filters.date_from || !filters.date_to) { setError('Informe o período'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await api.get('/time-records', { params: filters });
      setRecords(data);
    } catch { setError('Erro ao buscar registros'); }
    finally   { setLoading(false); }
  };

  const handleDownloadComprovante = async (record, evtKey, evtLabel, evtTime) => {
    setExporting({ id: record.id, key: evtKey });
    try { await exportComprovante(record, evtLabel, evtTime); }
    catch { setError('Erro ao gerar comprovante'); }
    finally { setExporting(null); }
  };

  // Expande registros em eventos individuais
  const events = records.flatMap((r) =>
    EVENT_TYPES
      .map((et) => ({ ...et, record: r, time: r[et.key] }))
      .filter((e) => e.time)
  );

  return (
    <div>
      <InfoBox>
        Comprovante por batida, disponível em até 48h após o registro conforme Portaria MTE 671/2021.
        Cada comprovante gerado em PDF (A5) contém NSR, horário exato e código de verificação.
      </InfoBox>
      <WarnBox>
        Assinatura PAdES: os PDFs gerados aqui <strong>não possuem assinatura digital</strong>.
        Para validade jurídica plena, submeta os arquivos a um serviço de assinatura ICP-Brasil.
      </WarnBox>

      <FilterForm filters={filters} setFilters={setFilters} employees={employees}
        departments={departments} showDept={false} empRequired />

      {error && <div className="alert-custom danger" style={{ marginBottom:12 }}>{error}</div>}

      <button onClick={handleBuscar} disabled={loading}
        style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer',
          fontSize:13, fontWeight:700, fontFamily:'inherit',
          background:'var(--surface-3)', color:'var(--text-1)', display:'flex', alignItems:'center', gap:6,
          opacity: loading ? 0.7 : 1, marginBottom:20 }}>
        <LuSearch /> {loading ? 'Buscando…' : 'Buscar Registros'}
      </button>

      {events.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:8 }}>
            {events.length} marcaç{events.length !== 1 ? 'ões' : 'ão'} encontrada{events.length !== 1 ? 's' : ''}
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                {['Data','Funcionário','Tipo de Marcação','Horário','NSR',''].map((h) => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', color:'var(--text-3)', fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const busy = exporting?.id === e.record.id && exporting?.key === e.key;
                return (
                  <tr key={`${e.record.id}-${e.key}`} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={(ev) => ev.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(ev) => ev.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--text-1)', whiteSpace:'nowrap' }}>
                      {fmtDateLong(e.record.work_date)}
                    </td>
                    <td style={{ padding:'8px 10px', color:'var(--text-2)' }}>
                      {e.record.employee?.full_name || '—'}
                    </td>
                    <td style={{ padding:'8px 10px' }}>
                      <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                        background: e.bg, color: e.color }}>
                        {e.label}
                      </span>
                    </td>
                    <td style={{ padding:'8px 10px', fontWeight:700, color:'var(--text-1)' }}>
                      {new Date(e.time).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                    </td>
                    <td style={{ padding:'8px 10px', color:'var(--text-3)', fontFamily:'monospace', fontSize:11 }}>
                      {e.record.nsr ? String(e.record.nsr).padStart(9,'0') : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'right' }}>
                      <button
                        onClick={() => handleDownloadComprovante(e.record, e.key, e.label, e.time)}
                        disabled={!!exporting}
                        title="Baixar comprovante em PDF"
                        style={{ padding:'5px 12px', border:'1px solid var(--border)', borderRadius:6,
                          background: busy ? 'var(--accent-soft)' : 'var(--surface-1)', cursor:'pointer',
                          fontSize:11, fontWeight:600, fontFamily:'inherit', color:'var(--text-1)',
                          display:'inline-flex', alignItems:'center', gap:4,
                          opacity: exporting && !busy ? 0.5 : 1 }}>
                        <LuFileText size={12} color="var(--danger)" />
                        {busy ? 'Gerando…' : 'PDF'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && records.length > 0 && events.length === 0 && (
        <div className="empty-state">
          <LuReceipt size={28} color="var(--text-3)" style={{ marginBottom:8 }} />
          <p>Nenhuma marcação no período selecionado</p>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'espelho',      label: 'Espelho de Ponto',     icon: <LuContact /> },
  { id: 'afd',          label: 'AFD',                   icon: <LuFileText /> },
  { id: 'aej',          label: 'AEJ',                   icon: <LuFileCode /> },
  { id: 'comprovante',  label: 'Comprovante',           icon: <LuReceipt />  },
];

export default function ReportsPage() {
  const [activeTab,    setActiveTab]    = useState('espelho');
  const [employees,    setEmployees]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [metaError,    setMetaError]    = useState('');
  const [filters, setFilters] = useState(() => ({
    employee_id: '', department_id: '', ...defaultPeriod(),
  }));

  useEffect(() => {
    Promise.all([api.get('/employees'), api.get('/departments')])
      .then(([eRes, dRes]) => {
        setEmployees(eRes.data.filter((e) => e.active !== false));
        setDepartments(dRes.data);
      })
      .catch(() => setMetaError('Erro ao carregar funcionários/departamentos'));
  }, []);

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Relatórios Legais</div>
          <div className="page-card-subtitle">
            Portaria MTE 671/2021 — Espelho, AFD, AEJ e Comprovante de Marcação
          </div>
        </div>
      </div>

      {metaError && <div className="alert-custom danger" style={{ marginBottom:12 }}>{metaError}</div>}

      {/* Tab navigation */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'2px solid var(--border)', flexWrap:'wrap' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              display:'flex', alignItems:'center', gap:6,
              padding:'9px 18px', border:'none', cursor:'pointer', fontFamily:'inherit',
              fontSize:13, fontWeight: activeTab === t.id ? 700 : 400,
              background:'transparent',
              color:      activeTab === t.id ? 'var(--accent)' : 'var(--text-2)',
              borderBottom: activeTab === t.id ? '3px solid var(--accent)' : '3px solid transparent',
              marginBottom: -2, borderRadius:'4px 4px 0 0',
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'espelho'     && <TabEspelho    filters={filters} setFilters={setFilters} employees={employees} departments={departments} />}
      {activeTab === 'afd'         && <TabAFD        filters={filters} setFilters={setFilters} employees={employees} departments={departments} />}
      {activeTab === 'aej'         && <TabAEJ        filters={filters} setFilters={setFilters} employees={employees} departments={departments} />}
      {activeTab === 'comprovante' && <TabComprovante filters={filters} setFilters={setFilters} employees={employees} departments={departments} />}
    </div>
  );
}
